import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TaxonomySelect } from '../../components/admin/TaxonomySelect';
import { API_BASE } from '../../config/api';

/**
 * Phase 1 — AI Data Generator
 * Admin UI for initiating AI-powered data generation via n8n webhook
 * This page is logically separate from existing CRUD pages
 */

export const AIDataGenerator = () => {
  const [formData, setFormData] = useState({
    entity_type: '',
    entity_name: '',
    country: '',
    sources: [{ type: 'url', label: '', value: '', file: null }],
    ai_notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [executionLogs, setExecutionLogs] = useState([]);

  const backendUrl = API_BASE;
  const token = localStorage.getItem('admin_token');

  // Load logs from localStorage on mount
  useEffect(() => {
    const savedLogs = localStorage.getItem('ai_generation_logs');
    if (savedLogs) {
      try {
        const parsedLogs = JSON.parse(savedLogs);
        setExecutionLogs(parsedLogs);
        
        // Start polling for any pending/checking logs
        startGlobalPolling(parsedLogs);
      } catch (e) {
        console.error('Failed to parse saved logs:', e);
      }
    }
  }, []);

  // Save logs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('ai_generation_logs', JSON.stringify(executionLogs));
  }, [executionLogs]);

  // Global polling effect - checks all pending/checking logs periodically
  useEffect(() => {
    const pollingInterval = setInterval(() => {
      // Use functional update to get latest logs
      setExecutionLogs(currentLogs => {
        // Find all logs that need status updates
        const logsToCheck = currentLogs.filter(log => 
          (log.draftStatus === 'pending' || log.draftStatus === 'checking') &&
          log.submissionStatus === 'success'
        );

        if (logsToCheck.length > 0) {
          // Check each pending/checking log
          logsToCheck.forEach(log => {
            checkDraftStatusForLog(log, currentLogs);
          });
        }
        
        return currentLogs; // Return unchanged, updates happen in checkDraftStatusForLog
      });
    }, 15000); // Poll every 15 seconds

    return () => clearInterval(pollingInterval);
  }, []); // Empty dependency array - interval runs independently

  // Function to add a log entry
  const addLogEntry = (entry) => {
    const newEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
      ...entry
    };
    setExecutionLogs(prev => [newEntry, ...prev]); // Keep all logs
  };

  // Function to update a log entry
  const updateLogEntry = (id, updates) => {
    setExecutionLogs(prev => 
      prev.map(log => log.id === id ? { ...log, ...updates } : log)
    );
  };

  // Function to check if draft is registered in admin UI for the selected entity type
  const checkDraftStatusForLog = async (log, allLogs) => {
    const { entityType, entityName, id: logId, timestamp, submissionCompletedAt } = log;
    
    try {
      // Map entity types to admin UI list endpoints
      const adminListEndpoints = {
        university: 'universities',
        program: 'programs',
        scholarship: 'scholarships'
      };
      
      const endpoint = adminListEndpoints[entityType];
      if (!endpoint) {
        updateLogEntry(logId, {
          draftStatusMessage: `Unknown entity type: ${entityType}`
        });
        return false;
      }

      // Query the admin UI list endpoint (same endpoint used by admin UI lists)
      const response = await fetch(`${backendUrl}/api/admin/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          updateLogEntry(logId, {
            draftStatusMessage: 'Authentication required - please log in'
          });
          return false;
        }
        throw new Error(`Failed to fetch ${endpoint} list: ${response.status}`);
      }

      const data = await response.json();
      // Backend returns: {"success": true, "count": N, "universities": [...]} or similar
      const items = data[endpoint] || data[`${endpoint}`] || [];
      
      // Find draft matching entity name (case-insensitive, partial match)
      // Check if draft is registered in admin UI (visible in the list)
      const draft = items.find(item => {
        // Get the name field based on entity type
        const itemName = entityType === 'program' 
          ? item.program_name?.toLowerCase() 
          : item.name?.toLowerCase();
        const searchName = entityName.toLowerCase();
        const nameMatches = itemName && (
          itemName.includes(searchName) || 
          searchName.includes(itemName) ||
          itemName === searchName
        );
        
        // Check if it's an AI-generated draft (not published)
        const isAiDraft = item._source === 'ai' && item.published === false;
        
        // If we have a timestamp, prefer drafts created after the log timestamp
        if (timestamp && item.created_at) {
          const itemCreated = new Date(item.created_at);
          const logCreated = new Date(timestamp);
          // Allow 30 seconds buffer for processing time
          const bufferTime = 30 * 1000; // 30 seconds in milliseconds
          return nameMatches && isAiDraft && itemCreated >= (logCreated - bufferTime);
        }
        
        return nameMatches && isAiDraft;
      });

      if (draft) {
        // Draft is registered in admin UI - update status
        updateLogEntry(logId, {
          draftStatus: 'saved',
          draftId: draft.id,
          draftFoundAt: new Date().toISOString(),
          draftStatusMessage: `Draft registered in ${entityType} admin UI`
        });
        return true;
      } else {
        // Draft not yet visible in admin UI - update checking status
        if (log.draftStatus === 'checking' || log.draftStatus === 'pending') {
          // Calculate how long we've been checking
          const checkStartTime = submissionCompletedAt 
            ? new Date(submissionCompletedAt).getTime()
            : new Date(timestamp).getTime();
          const elapsedSeconds = Math.floor((Date.now() - checkStartTime) / 1000);
          const elapsedMinutes = Math.floor(elapsedSeconds / 60);
          
          // Timeout after 5 minutes
          if (elapsedMinutes >= 5) {
            updateLogEntry(logId, {
              draftStatus: 'timeout',
              draftStatusMessage: `Draft not registered in admin UI after ${elapsedMinutes} minutes`
            });
            return false;
          }
          
          // Update to checking status if still pending
          if (log.draftStatus === 'pending') {
            updateLogEntry(logId, {
              draftStatus: 'checking',
              draftStatusMessage: `Checking admin UI... (${elapsedSeconds}s elapsed)`
            });
          } else {
            updateLogEntry(logId, {
              draftStatusMessage: `Checking admin UI... (${elapsedSeconds}s elapsed)`
            });
          }
        }
      }
    } catch (err) {
      console.error('Error checking draft status in admin UI:', err);
      updateLogEntry(logId, {
        draftStatusMessage: `Error checking admin UI: ${err.message}`
      });
    }
    return false;
  };

  // Wrapper function for backward compatibility
  const checkDraftStatus = async (entityType, entityName, logId) => {
    const log = executionLogs.find(l => l.id === logId);
    if (log) {
      return await checkDraftStatusForLog(log, executionLogs);
    }
    return false;
  };

  // Function to start global polling for all pending logs
  const startGlobalPolling = (logs) => {
    // This is handled by the useEffect polling mechanism
    // No need for individual polling intervals
  };

  const handleEntityTypeChange = (e) => {
    setFormData({ ...formData, entity_type: e.target.value });
  };

  const handleEntityNameChange = (e) => {
    setFormData({ ...formData, entity_name: e.target.value });
  };

  const handleCountryChange = (value) => {
    setFormData({ ...formData, country: value });
  };

  const handleAddSource = () => {
    setFormData({
      ...formData,
      sources: [...formData.sources, { type: 'url', label: '', value: '', file: null }]
    });
  };

  const handleRemoveSource = (index) => {
    const newSources = formData.sources.filter((_, i) => i !== index);
    setFormData({ ...formData, sources: newSources });
  };

  const handleSourceChange = (index, field, value) => {
    setFormData(prev => {
      const newSources = [...prev.sources];
      newSources[index] = { ...newSources[index], [field]: value };
      return { ...prev, sources: newSources };
    });
  };

  const handleFileChange = async (index, file) => {
    if (!file) return;

    // Validate file type before upload
    const filename = file.name || '';
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const sourceType = formData.sources[index]?.type;
    
    // Validate extension matches source type
    const typeExtensions = {
      'pdf': ['pdf'],
      'xlsx': ['xlsx'],
      'csv': ['csv'],
      'xls': ['xls']
    };
    
    if (sourceType && typeExtensions[sourceType] && !typeExtensions[sourceType].includes(extension)) {
      setError(`Invalid file type. Expected ${sourceType.toUpperCase()}, got ${extension.toUpperCase()}`);
      return;
    }

    // Set uploading state using functional update
    setUploadingFiles(prev => ({ ...prev, [index]: true }));
    setError(null); // Clear previous errors

    // Create an AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

    try {
      const formDataFile = new FormData();
      formDataFile.append('file', file);

      console.log(`Starting upload for source ${index + 1}:`, filename, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);

      const response = await fetch(`${backendUrl}/api/admin/ai/upload-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Do NOT set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formDataFile,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = '';
        let errorDetail = '';
        
        // Try to parse error response
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || '';
          errorMessage = errorDetail || `Server error: ${response.status} ${response.statusText}`;
        } catch (e) {
          // If JSON parsing fails, try to get text response
          try {
            const text = await response.text();
            if (text) {
              errorDetail = text.substring(0, 200);
              errorMessage = errorDetail;
            } else {
              errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
          } catch (textErr) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        }
        
        // Add context based on HTTP status code
        if (!errorDetail) {
          switch (response.status) {
            case 400:
              errorMessage = `Invalid file: ${errorMessage || 'Please check the file format and size'}`;
              break;
            case 401:
              errorMessage = 'Authentication failed. Please log in again.';
              break;
            case 403:
              errorMessage = errorDetail || 'Permission denied. Please contact support.';
              break;
            case 409:
              errorMessage = errorDetail || 'File already exists. Please try uploading again.';
              break;
            case 413:
              errorMessage = errorDetail || 'File too large. Maximum size is 50MB.';
              break;
            case 500:
              errorMessage = errorDetail || 'Server error occurred. Please try again or contact support.';
              break;
            case 503:
              errorMessage = errorDetail || 'Service temporarily unavailable. Please try again in a moment.';
              break;
            case 504:
              errorMessage = errorDetail || 'Request timed out. Please try again or use a smaller file.';
              break;
            default:
              errorMessage = errorDetail || `Upload failed: ${response.status} ${response.statusText}`;
          }
        }
        
        console.error(`Upload failed for "${filename}": [${response.status}] ${errorMessage}`);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      console.log('Upload response:', data);
      
      if (!data.file_id) {
        console.error('Server response missing file_id:', data);
        throw new Error('Server did not return file_id. Response: ' + JSON.stringify(data));
      }
      
      const fileId = data.file_id;
      console.log(`File uploaded successfully. Index: ${index + 1}, File ID: ${fileId}`);
      
      // Update both value and file in a single state update to avoid race conditions
      setFormData(prev => {
        const newSources = [...prev.sources];
        newSources[index] = {
          ...newSources[index],
          value: fileId,
          file: null // Clear file object after successful upload
        };
        console.log('Updated source:', newSources[index]);
        return { ...prev, sources: newSources };
      });
      
      setSuccess(`File "${filename}" uploaded successfully`);
    } catch (err) {
      clearTimeout(timeoutId);
      console.error(`File upload error for "${filename}":`, err);
      
      let errorMessage = err.message || 'File upload failed';
      
      // Handle specific error types with detailed messages
      if (err.name === 'AbortError') {
        errorMessage = `Upload timeout for "${filename}". The file may be too large (${(file.size / 1024 / 1024).toFixed(2)} MB) or the connection is slow. Please try again or use a smaller file.`;
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.message.includes('Network request failed')) {
        errorMessage = `Network error while uploading "${filename}". Please check your internet connection and try again.`;
      } else if (err.message.includes('timeout') || err.message.includes('timed out')) {
        errorMessage = `Upload timed out for "${filename}". Please try again or use a smaller file.`;
      } else if (err.message.includes('File too large') || err.message.includes('exceeds maximum')) {
        errorMessage = `"${filename}" is too large. ${err.message}`;
      } else if (err.message.includes('Invalid file type') || err.message.includes('Invalid file')) {
        errorMessage = `"${filename}": ${err.message}`;
      } else if (err.message.includes('Database') && !err.message.includes('Database connection issue:')) {
        // Only add context if the error message doesn't already include our prefix
        // Backend already provides clear error messages, just use them as-is
        if (!err.message.startsWith('"')) {
          errorMessage = `"${filename}": ${err.message}`;
        } else {
          errorMessage = err.message;
        }
      } else if (err.message.includes('Server error')) {
        errorMessage = `Server error while uploading "${filename}": ${err.message}`;
      } else if (!err.message.startsWith('"')) {
        // If error message doesn't already include filename, add it for context
        errorMessage = `"${filename}": ${errorMessage}`;
      }
      
      console.error(`Final error message: ${errorMessage}`);
      setError(errorMessage);
      
      // Clear the file from state if upload failed
      setFormData(prev => {
        const newSources = [...prev.sources];
        newSources[index] = {
          ...newSources[index],
          file: null,
          value: ''
        };
        return { ...prev, sources: newSources };
      });
    } finally {
      // Use functional update to avoid stale closure - ALWAYS execute to clear uploading state
      setUploadingFiles(prev => {
        const updated = { ...prev, [index]: false };
        console.log(`Cleared uploading state for source ${index + 1}`);
        return updated;
      });
    }
  };

  const handleAiNotesChange = (e) => {
    setFormData({ ...formData, ai_notes: e.target.value });
  };

  // Handle resubmit from log entry
  const handleResubmit = async (log) => {
    if (!log.originalFormData) {
      setError('Cannot resubmit: Original form data not found');
      return;
    }

    // Confirm resubmission
    if (!window.confirm(`Resubmit AI generation for "${log.entityName}"? This will create a new draft.`)) {
      return;
    }

    // Populate form with original data
    const originalData = log.originalFormData;
    setFormData({
      entity_type: originalData.entity_type,
      entity_name: originalData.entity_name,
      country: originalData.country,
      sources: originalData.sources.map(s => ({
        type: s.type,
        label: s.label,
        value: s.value,
        file: null // Files need to be re-uploaded if they're file IDs
      })),
      ai_notes: originalData.ai_notes || ''
    });

    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Auto-submit after a short delay to allow form to populate
    setTimeout(() => {
      // Trigger form submission
      const form = document.querySelector('form');
      if (form) {
        // Validate and submit
        if (validateForm()) {
          confirmSubmit();
        } else {
          setError('Cannot resubmit: Some required fields are missing. Please check the form.');
        }
      }
    }, 500);
  };

  const validateForm = () => {
    if (!formData.entity_type) {
      setError('Please select an entity type');
      return false;
    }
    if (!formData.entity_name.trim()) {
      setError('Please enter an entity name');
      return false;
    }
    if (!formData.country) {
      setError('Please select a country');
      return false;
    }
    if (formData.sources.length === 0) {
      setError('Please add at least one source');
      return false;
    }
    for (let i = 0; i < formData.sources.length; i++) {
      const source = formData.sources[i];
      if (!source.label.trim()) {
        setError(`Source ${i + 1}: Please enter a label/description`);
        return false;
      }
      if (source.type === 'url' && !source.value.trim()) {
        setError(`Source ${i + 1}: Please enter a URL`);
        return false;
      }
      if (source.type !== 'url' && !source.value && !source.file) {
        setError(`Source ${i + 1}: Please upload a file or enter a URL`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    const entityType = formData.entity_type.toLowerCase();
    const entityName = formData.entity_name.trim();
    const timestamp = new Date().toISOString();

    // Create log entry (store original form data for resubmission)
    const logId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    addLogEntry({
      id: logId,
      entityType: entityType,
      entityName: entityName,
      country: formData.country,
      sourcesCount: formData.sources.length,
      submissionStatus: 'pending',
      draftStatus: 'pending',
      timestamp: timestamp,
      // Store original form data for resubmission
      originalFormData: {
        entity_type: entityType,
        entity_name: entityName,
        country: formData.country,
        sources: formData.sources.map(s => ({
          type: s.type,
          label: s.label,
          value: s.value, // File ID or URL
          file: null // Don't store file object, will re-upload if needed
        })),
        ai_notes: formData.ai_notes
      }
    });

    try {
      // Prepare sources payload
      const sourcesPayload = await Promise.all(
        formData.sources.map(async (source) => {
          let value = source.value;
          
          // If file was uploaded but not yet saved, upload it now
          if (source.file && !source.value) {
            const formDataFile = new FormData();
            formDataFile.append('file', source.file);
            const uploadResponse = await fetch(`${backendUrl}/api/admin/ai/upload-file`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formDataFile
            });
            if (!uploadResponse.ok) {
              throw new Error(`File upload failed for ${source.label}`);
            }
            const uploadData = await uploadResponse.json();
            value = uploadData.file_id;
          }

          return {
            type: source.type,
            file_type: source.type, // Add file_type field for webhook
            label: source.label.trim(),
            value: value
          };
        })
      );

      const payload = {
        entity_type: entityType,
        entity_name: entityName,
        country: formData.country,
        sources: sourcesPayload,
        ai_notes: formData.ai_notes.trim() || null
      };

      // Update log: submission started
      updateLogEntry(logId, {
        submissionStatus: 'submitting',
        submissionStatusMessage: 'Sending request to n8n webhook...'
      });

      const response = await fetch(`${backendUrl}/api/admin/ai/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Failed to initiate AI generation';
        
        // Update log: submission failed
        updateLogEntry(logId, {
          submissionStatus: 'failed',
          submissionStatusMessage: errorMessage,
          submissionFailedAt: new Date().toISOString()
        });
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Update log: submission successful
      updateLogEntry(logId, {
        submissionStatus: 'success',
        submissionStatusMessage: data.message || 'AI generation initiated successfully',
        submissionCompletedAt: new Date().toISOString(),
        draftStatus: 'checking',
        draftStatusMessage: 'Waiting for draft to be saved...'
      });

      setSuccess(data.message || 'AI generation initiated successfully');
      
      // Immediately check once after 5 seconds, then global polling will continue every 15 seconds
      setTimeout(() => {
        setExecutionLogs(currentLogs => {
          const log = currentLogs.find(l => l.id === logId);
          if (log && (log.draftStatus === 'checking' || log.draftStatus === 'pending')) {
            checkDraftStatusForLog(log, currentLogs);
          }
          return currentLogs;
        });
      }, 5000);
      
      // Reset form
      setFormData({
        entity_type: '',
        entity_name: '',
        country: '',
        sources: [{ type: 'url', label: '', value: '', file: null }],
        ai_notes: ''
      });
    } catch (err) {
      setError(err.message || 'Failed to initiate AI generation');
    } finally {
      setLoading(false);
    }
  };

  // Function to clear logs
  const clearLogs = () => {
    if (window.confirm('Are you sure you want to clear all execution logs?')) {
      setExecutionLogs([]);
      localStorage.removeItem('ai_generation_logs');
    }
  };

  // Function to format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'success':
      case 'saved':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'timeout':
        return 'bg-red-100 text-red-800';
      case 'pending':
      case 'checking':
      case 'submitting':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="bg-white rounded-xl border-2 border-[#E5E7EB] p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#111827] mb-2">AI Data Generator</h1>
          <p className="text-[#6B7280]">
            Generate AI-powered drafts for universities, programs, or scholarships. Output will be saved as draft for human review.
          </p>
          <div className="mt-4 p-4 bg-[#FEF3C7] border border-[#FCD34D] rounded-lg">
            <p className="text-sm text-[#92400E]">
              <strong>Note:</strong> AI output will be saved as draft for human review. No data will be published automatically.
            </p>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section A — Entity Selection */}
          <section className="border-b-2 border-[#E5E7EB] pb-8">
            <h2 className="text-xl font-bold text-[#111827] mb-4">Entity Selection</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Entity Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.entity_type}
                  onChange={handleEntityTypeChange}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                  required
                >
                  <option value="">Select entity type...</option>
                  <option value="university">University</option>
                  <option value="program">Program</option>
                  <option value="scholarship">Scholarship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Entity Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={handleEntityNameChange}
                  placeholder="e.g. University of Malaya"
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <TaxonomySelect
                  type="country"
                  value={formData.country}
                  onChange={handleCountryChange}
                  multiple={false}
                  label="Country"
                  placeholder="Select country..."
                  required={true}
                />
              </div>
            </div>
          </section>

          {/* Section B — Source Inputs */}
          <section className="border-b-2 border-[#E5E7EB] pb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#111827]">Source Inputs</h2>
              <button
                type="button"
                onClick={handleAddSource}
                className="px-4 py-2 bg-[#1E40AF] text-white rounded-lg text-sm font-semibold hover:bg-[#1E3A8A] transition-colors"
              >
                + Add Source
              </button>
            </div>

            <div className="space-y-4">
              {formData.sources.map((source, index) => (
                <div key={index} className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-semibold text-[#6B7280]">Source {index + 1}</span>
                    {formData.sources.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSource(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-semibold"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">
                        Source Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={source.type}
                        onChange={(e) => handleSourceChange(index, 'type', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        required
                      >
                        <option value="url">URL</option>
                        <option value="pdf">.pdf</option>
                        <option value="xlsx">xlsx</option>
                        <option value="csv">csv</option>
                        <option value="xls">xls</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">
                        Source Label / Description <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={source.label}
                        onChange={(e) => handleSourceChange(index, 'label', e.target.value)}
                        placeholder="e.g. Official university website"
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      {source.type === 'url' ? (
                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">
                            URL <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="url"
                            value={source.value}
                            onChange={(e) => handleSourceChange(index, 'value', e.target.value)}
                            placeholder="https://example.com"
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                            required={source.type === 'url'}
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-semibold text-[#111827] mb-2">
                            File <span className="text-red-500">*</span>
                            {uploadingFiles[index] && (
                              <span className="ml-2 text-[#6B7280] text-xs">(Uploading...)</span>
                            )}
                          </label>
                          <input
                            type="file"
                            key={`file-input-${index}-${source.value || 'empty'}`}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                console.log(`File selected for source ${index + 1}:`, file.name, file.type); // Debug log
                                // Store file in state first
                                handleSourceChange(index, 'file', file);
                                // Then trigger upload (await to ensure it completes)
                                await handleFileChange(index, file);
                              } else {
                                // Clear file if no file selected
                                handleSourceChange(index, 'file', null);
                                handleSourceChange(index, 'value', '');
                              }
                            }}
                            accept={
                              source.type === 'pdf' ? '.pdf,application/pdf' :
                              source.type === 'xlsx' ? '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                              source.type === 'csv' ? '.csv,text/csv' :
                              source.type === 'xls' ? '.xls,application/vnd.ms-excel' : '*'
                            }
                            className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1E40AF] file:text-white hover:file:bg-[#1E3A8A]"
                            disabled={uploadingFiles[index]}
                          />
                          {source.value && (
                            <p className="mt-2 text-xs text-green-600 font-medium">
                              ✓ File uploaded successfully (ID: {source.value.substring(0, 8)}...)
                            </p>
                          )}
                          {uploadingFiles[index] && (
                            <p className="mt-2 text-xs text-[#6B7280]">
                              Uploading... Please wait.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section C — Optional AI Guidance */}
          <section className="border-b-2 border-[#E5E7EB] pb-8">
            <h2 className="text-xl font-bold text-[#111827] mb-4">Optional AI Guidance</h2>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">
                Optional Notes for AI
              </label>
              <textarea
                value={formData.ai_notes}
                onChange={handleAiNotesChange}
                placeholder="Describe what the AI should focus on (e.g. accreditation, exemptions, career outcomes)."
                rows={4}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
              />
              <p className="mt-2 text-xs text-[#6B7280]">
                Describe what the AI should focus on (e.g. accreditation, exemptions, career outcomes).
              </p>
            </div>
          </section>

          {/* Section D — Action */}
          <section>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[#1E40AF] text-white rounded-lg font-semibold hover:bg-[#1E3A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Generating...' : 'Generate AI Draft'}
            </button>
          </section>
        </form>

        {/* Execution Log Section */}
        <div className="mt-8 border-t-2 border-[#E5E7EB] pt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#111827]">Execution Log</h2>
              <p className="text-sm text-[#6B7280] mt-1">
                Track AI generation submissions and draft save status
              </p>
            </div>
            {executionLogs.length > 0 && (
              <button
                onClick={clearLogs}
                className="px-4 py-2 text-sm font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear Logs
              </button>
            )}
          </div>

          {executionLogs.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">
              <p>No execution logs yet. Submit an AI generation request to see logs here.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              <div className="text-xs text-[#6B7280] mb-2 px-2">
                Showing {executionLogs.length} log{executionLogs.length !== 1 ? 's' : ''} • Auto-refreshing every 15 seconds
              </div>
              {executionLogs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] hover:border-[#1E40AF] transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-[#111827] capitalize">
                          {log.entityType}
                        </span>
                        <span className="text-sm font-medium text-[#6B7280]">
                          {log.entityName}
                        </span>
                        {log.country && (
                          <span className="text-xs text-[#6B7280]">
                            ({log.country})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6B7280] mb-2">
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    {/* Submission Status */}
                    <div>
                      <div className="text-xs font-semibold text-[#6B7280] mb-1">
                        Submission Status
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(log.submissionStatus)}`}>
                          {log.submissionStatus === 'success' ? '✓ Submitted' :
                           log.submissionStatus === 'failed' ? '✗ Failed' :
                           log.submissionStatus === 'submitting' ? '⟳ Submitting...' :
                           '⏳ Pending'}
                        </span>
                        {log.submissionStatusMessage && (
                          <span className="text-xs text-[#6B7280]">
                            {log.submissionStatusMessage}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Draft Status */}
                    <div>
                      <div className="text-xs font-semibold text-[#6B7280] mb-1">
                        Draft Status
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(log.draftStatus)}`}>
                          {log.draftStatus === 'saved' ? '✓ Draft Saved' :
                           log.draftStatus === 'timeout' ? '⏱ Timeout' :
                           log.draftStatus === 'checking' ? '⟳ Checking...' :
                           '⏳ Pending'}
                        </span>
                        {log.draftStatusMessage && (
                          <span className="text-xs text-[#6B7280]">
                            {log.draftStatusMessage}
                          </span>
                        )}
                      </div>
                      {log.draftId && (
                        <div className="text-xs text-[#6B7280] mt-1">
                          Draft ID: {log.draftId.substring(0, 8)}...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-[#6B7280]">
                        Sources: {log.sourcesCount || 0} | 
                        {log.submissionCompletedAt && (
                          <span> Submitted: {formatTimestamp(log.submissionCompletedAt)}</span>
                        )}
                        {log.draftFoundAt && (
                          <span> | Draft Found: {formatTimestamp(log.draftFoundAt)}</span>
                        )}
                      </div>
                      {/* Resubmit Button */}
                      {log.originalFormData && (
                        <button
                          onClick={() => handleResubmit(log)}
                          disabled={loading || log.submissionStatus === 'submitting'}
                          className="px-3 py-1.5 text-xs font-semibold text-[#1E40AF] bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Resubmit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Confirm AI Generation</h3>
            <p className="text-[#6B7280] mb-6">
              This will generate a DRAFT using AI. No data will be published.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] text-[#6B7280] rounded-lg font-semibold hover:bg-[#F9FAFB] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="flex-1 px-4 py-2 bg-[#1E40AF] text-white rounded-lg font-semibold hover:bg-[#1E3A8A] transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AIDataGenerator;
